/* eslint-disable no-await-in-loop */
import React, { useEffect, useRef, useState } from 'react';
import Text from './Text/index';
import BubbleOptions from './BubbleOptions';
import ImageOptions from './ImageOptions';
import { NODE_TYPES } from '../../../config/caf';
import { ContextMetaInfo, OptionClickInfo, ResponseObj } from '../../../types/caf';
import Button from '../../Button';
import { ButtonProps } from '../../Button/Button';

type Props = {
  conversation: ResponseObj[];
  conversationId: string;
  totalConversations: number;
  scrollToBottom: (ref: React.MutableRefObject<HTMLDivElement | null>) => void;
  modalRef: React.MutableRefObject<HTMLDivElement>;
  setHeight: React.Dispatch<React.SetStateAction<number>>;
  handleOptionClick: (info: OptionClickInfo) => void;
  contextMetaInfo: ContextMetaInfo | {};
  isLastConversation: boolean;
};

export const Conversation = (props: Props): JSX.Element => {
  const [singleConversation, setSingleConversation] = useState([]);
  const cafSectionRef = useRef(null);
  const animationFrameId = useRef(null);
  const timer = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));
  const isMounted = useRef(true);

  useEffect(() => {
    function handleMessageAnimation(): void {
      let i = 0;
      const animate = () => {
        if (!isMounted.current) {
          return;
        }

        if (i < props.conversation.length) {
          setSingleConversation((prevState) => [...prevState, props.conversation[i]]);

          i += 1;

          if (props.modalRef.current) {
            props.setHeight(props.modalRef.current.offsetHeight);
          }

          props.scrollToBottom(cafSectionRef);
          animationFrameId.current = requestAnimationFrame(animate);
        } else {
          cancelAnimationFrame(animationFrameId.current);
        }
      };

      animate();
    }

    isMounted.current = true;
    handleMessageAnimation();

    return () => {
      isMounted.current = false;
      cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  const handleNextButtonClick = (node: ResponseObj): void => {
    props.handleOptionClick({
      dispatchAction: node?.dispatchAction,
      info: {
        label: node?.value as string,
        value: node?.value as string
      },
      conversationId: props.conversationId,
      eventDetails: node?.eventDetails
    });
  };

  if (props.totalConversations !== 1 && props.isLastConversation) {
    return (
      <section className="caf-section" ref={cafSectionRef}>
      {
        singleConversation?.map((node: ResponseObj, index: number) => {
          switch (node.type) {
            case NODE_TYPES.TEXT:
              return (
                <React.Fragment key={index}>
                  <Text headings={node.value as string} headingClass={node.className} />
                </React.Fragment>
              );
            case NODE_TYPES.IMAGE_OPTIONS:
              return (
                <React.Fragment key={index}>
                  <ImageOptions
                    optionsObj={node}
                    contextMetaInfo={props.contextMetaInfo}
                    conversationId={props.conversationId}
                    handleOptionClick={props.handleOptionClick}
                    isLastConversation={props.isLastConversation}
                  />
                </React.Fragment>
              );
            case NODE_TYPES.BUBBLE_OPTIONS:
              return (
                <React.Fragment key={index}>
                  <BubbleOptions
                    optionsObj={node}
                    contextMetaInfo={props.contextMetaInfo}
                    conversationId={props.conversationId}
                    handleOptionClick={props.handleOptionClick}
                    isLastConversation={props.isLastConversation}
                  />
                </React.Fragment>
              );

            case NODE_TYPES.BUTTON_OPTIONS: {
              const nextButtonProps: ButtonProps = {
                classes: node.className,
                disabled: false,
                handleClick: () => handleNextButtonClick(node),
                id: '',
                text: node?.value?.toString()
              };

              return (
              <React.Fragment key= { index } >
                <Button {...nextButtonProps}/>
              </React.Fragment>
              );
            }

            default:
              return null;
          }
        })
      }
      </section>
    );
  }

  return (
    <section className="caf-section">
    {
      props.conversation.map((node: ResponseObj, index: number) => {
        switch (node.type) {
          case NODE_TYPES.TEXT:
            return (
              <React.Fragment key={index}>
                <Text headings={node.value as string} headingClass={node.className} />
              </React.Fragment>
            );
          case NODE_TYPES.IMAGE_OPTIONS:
            return (
              <React.Fragment key={index}>
                <ImageOptions
                  optionsObj={node}
                  contextMetaInfo={props.contextMetaInfo}
                  conversationId={props.conversationId}
                  handleOptionClick={props.handleOptionClick}
                  isLastConversation={props.isLastConversation}
                />
              </React.Fragment>
            );
          case NODE_TYPES.BUBBLE_OPTIONS:
            return (
              <React.Fragment key={index}>
                <BubbleOptions
                  optionsObj={node}
                  contextMetaInfo={props.contextMetaInfo}
                  conversationId={props.conversationId}
                  handleOptionClick={props.handleOptionClick}
                  isLastConversation={props.isLastConversation}
                />
              </React.Fragment>
            );

          case NODE_TYPES.BUTTON_OPTIONS: {
            const nextButtonProps: ButtonProps = {
              classes: node.className,
              disabled: false,
              handleClick: () => handleNextButtonClick,
              id: '',
              text: node?.value?.toString()
            };

            return (
              <React.Fragment key= {index}>
                <Button {...nextButtonProps} />
              </React.Fragment>
            );
          }

          default:
            return <></>;
        }
      })
    }
    </section>
  );
};
